/**
 * Main application component with routing
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignIn, SignUp, useAuth /*, useUser */ } from '@clerk/clerk-react';
import { setCurrentToken } from './services/api';

// Layout components
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './components/layout/AdminLayout';

// Public pages
import HomePage from './pages/HomePage';
import SoftwareCatalog from './pages/SoftwareCatalog';
import SoftwareDetails from './pages/SoftwareDetails';
import { Vulnerabilities } from './pages/Vulnerabilities';
import { CVEDetails } from './pages/CVEDetails';
import { OperatingSystems } from './pages/OperatingSystems';
import { OSProductDetails } from './pages/OSProductDetails';

// User pages
import ApiKeys from './pages/ApiKeys';
import DebugUser from './pages/DebugUser';
import AIAssistant from './pages/AIAssistant';
import Onboarding from './pages/Onboarding';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminJobs from './pages/admin/Jobs';
import AdminJobDetails from './pages/admin/JobDetails';
import AdminLogs from './pages/admin/Logs';
import AdminUsers from './pages/admin/Users';
import AdminSettings from './pages/admin/Settings';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [tokenReady, setTokenReady] = React.useState(false);

  React.useEffect(() => {
    if (isLoaded && isSignedIn) {
      // Wait for token to be available and set it
      getToken().then((token) => {
        if (token) {
          console.log('[ProtectedRoute] Got token, setting currentToken');
          setCurrentToken(token); // Set the token for RTK Query
          setTokenReady(true);
        }
      });
    } else if (isLoaded && !isSignedIn) {
      setCurrentToken(null);
    }
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded || (isSignedIn && !tokenReady)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyber-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return <>{children}</>;
}

// Admin route wrapper (requires admin role)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [tokenReady, setTokenReady] = React.useState(false);
  // const { user } = useUser(); // TODO: Uncomment when implementing proper role check

  React.useEffect(() => {
    if (isLoaded && isSignedIn) {
      // Wait for token to be available and set it
      getToken().then((token) => {
        if (token) {
          console.log('[AdminRoute] Got token, setting currentToken');
          setCurrentToken(token); // Set the token for RTK Query
          setTokenReady(true);
        }
      });
    } else if (isLoaded && !isSignedIn) {
      setCurrentToken(null);
    }
  }, [isLoaded, isSignedIn, getToken]);

  if (!isLoaded || (isSignedIn && !tokenReady)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-bg">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyber-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  // Check if user has admin role (stored in Clerk metadata)
  // TODO: Replace with actual auth check when Clerk role sync is working
  const isAdmin = true; // Temporary: allow all logged-in users to access admin
  // const isAdmin = user?.publicMetadata?.role === 'admin';
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes */}
        <Route
          path="/sign-in/*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-dark-bg">
              <SignIn routing="path" path="/sign-in" />
            </div>
          }
        />
        <Route
          path="/sign-up/*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-dark-bg">
              <SignUp routing="path" path="/sign-up" />
            </div>
          }
        />

        {/* Public routes */}
        <Route element={<MainLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/catalog" element={<SoftwareCatalog />} />
          <Route path="/software/:id" element={<SoftwareDetails />} />
          <Route path="/os" element={<OperatingSystems />} />
          <Route path="/os/products/:id" element={<OSProductDetails />} />
          <Route path="/vulnerabilities" element={<Vulnerabilities />} />
          <Route path="/vulnerability/:cve_id" element={<CVEDetails />} />
        </Route>

        {/* Protected user routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/ai-assistant" element={<AIAssistant />} />
          <Route path="/api-keys" element={<ApiKeys />} />
          <Route path="/debug-user" element={<DebugUser />} />
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="jobs" element={<AdminJobs />} />
          <Route path="jobs/:id" element={<AdminJobDetails />} />
          <Route path="logs" element={<AdminLogs />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="settings" element={<AdminSettings />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
